/**
 * Room Scheduler APIs (SCHEDULER.md).
 *
 * A schedule is *a saved, deferred press of the room's Run button*. These
 * endpoints manage the schedule rows + their EventBridge clock; results stream
 * to the room via AppSync as usual (the Scheduler tab shows job state, not
 * results). Response shapes mirror ``pool_schedules_impl.py``:
 *
 *   GET    /networks/{id}/pools/{poolId}/schedules                 → { schedules, count }
 *   POST   /networks/{id}/pools/{poolId}/schedules                 → schedule
 *   GET    /networks/{id}/pools/{poolId}/schedules/{scheduleId}    → schedule
 *   PUT    /networks/{id}/pools/{poolId}/schedules/{scheduleId}    → schedule
 *   DELETE /networks/{id}/pools/{poolId}/schedules/{scheduleId}    → { deleted, scheduleId }
 *   POST   .../{scheduleId}/toggle   { enabled? }                  → schedule
 *   GET    .../{scheduleId}/runs?limit=N                           → { runs, count }
 *   POST   .../{scheduleId}/run-now                                → { triggered }
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function encodePath(s) {
  return encodeURIComponent(String(s || '').trim());
}

async function authedFetch(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || data.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function _base(networkId, poolId) {
  return `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/schedules`;
}

export async function listPoolSchedules(networkId, poolId) {
  return authedFetch(_base(networkId, poolId));
}

/**
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ prompt: string, cadence: object, enabled?: boolean, inputExtra?: object }} body
 */
export async function createPoolSchedule(networkId, poolId, body) {
  return authedFetch(_base(networkId, poolId), {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
}

export async function getPoolSchedule(networkId, poolId, scheduleId) {
  return authedFetch(`${_base(networkId, poolId)}/${encodePath(scheduleId)}`);
}

export async function updatePoolSchedule(networkId, poolId, scheduleId, body) {
  return authedFetch(`${_base(networkId, poolId)}/${encodePath(scheduleId)}`, {
    method: 'PUT',
    body: JSON.stringify(body || {}),
  });
}

export async function deletePoolSchedule(networkId, poolId, scheduleId) {
  return authedFetch(`${_base(networkId, poolId)}/${encodePath(scheduleId)}`, {
    method: 'DELETE',
  });
}

/**
 * Flip a schedule on/off. Pass ``enabled`` explicitly; omit to toggle current.
 */
export async function togglePoolSchedule(networkId, poolId, scheduleId, enabled) {
  const body = typeof enabled === 'boolean' ? { enabled } : {};
  return authedFetch(`${_base(networkId, poolId)}/${encodePath(scheduleId)}/toggle`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function listPoolScheduleRuns(networkId, poolId, scheduleId, limit = 20) {
  const qs = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
  return authedFetch(`${_base(networkId, poolId)}/${encodePath(scheduleId)}/runs${qs}`);
}

export async function runPoolScheduleNow(networkId, poolId, scheduleId) {
  return authedFetch(`${_base(networkId, poolId)}/${encodePath(scheduleId)}/run-now`, {
    method: 'POST',
  });
}
